import { createSlice } from "@reduxjs/toolkit";
import { createUser, getUser, loginUser, logoutUser, refreshUser, demoLoginUser, forgotPassword, resetPassword, resendConfirmation, updateUser, uploadAvatar, cookieStatus } from "../actions/userActionsHelper";
import { usersObject } from "../stateObject/usersObject";

const initialState = usersObject;

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    setLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    clearSignupSuccess: (state) => {
      state.isSignupSuccess = false;
    },
    clearForgotPasswordState: (state) => {
      state.isForgotPasswordSuccess = false;
      state.forgotPasswordError = null;
    },
    clearResetPasswordState: (state) => {
      state.isResetPasswordSuccess = false;
      state.resetPasswordError = null;
    },
    clearResendConfirmationState: (state) => {
      state.isResendConfirmationSuccess = false;
      state.resendConfirmationError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getUser.request, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getUser.success, (state, action) => {
        state.userData = action.payload;
        state.isLoading = false;
        state.error = null;
      })
      .addCase(getUser.failed, (state) => {
        state.isLoading = false;
      })
      .addCase(loginUser.request, (state) => {
        state.isLoading = true;
        state.error = null; // Clear previous errors
        // Reset cookie tracking on new login
        state.cookieSetHeaderSent = false;
        state.cookieConfirmed = false;
      })
      .addCase(loginUser.success, (state, action) => {
        state.userData = action.payload;
        state.isLoading = false;
        state.error = null;
      })
      .addCase(loginUser.failed, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Login failed. Please check your credentials.";
      })
      .addCase(createUser.request, (state) => {
        state.isLoading = true;
        state.error = null; // Clear previous errors
        state.isSignupSuccess = false; // Reset signup success state
      })
      .addCase(createUser.success, (state, action) => {
        state.userData = action.payload;
        state.isLoading = false;
        state.error = null;
        state.isSignupSuccess = true; // Set signup success state
      })
      .addCase(createUser.failed, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "User creation failed. Please try again.";
        state.isSignupSuccess = false; // Reset signup success state on failure
      })
      .addCase(logoutUser.request, (state) => {
        state.isLoading = true;
      })
      .addCase(logoutUser.success, () => {
        return initialState; // Reset state to initial state on logout
      })
      .addCase(logoutUser.failed, (state) => {
        state.isLoading = false;
      })
      .addCase(refreshUser.request, (state) => {
        state.isRefreshing = true;
        state.error = null;
      })
      .addCase(refreshUser.success, (state, action) => {
        // Update user data if provided, otherwise keep existing
        if (action.payload.userData) {
          state.userData = action.payload.userData;
        }
        state.isRefreshing = false;
        state.error = null;
      })
      .addCase(refreshUser.failed, (state, action) => {
        state.isRefreshing = false;
        // Don't set error for failed refresh - silent failure
      })
      .addCase(forgotPassword.request, (state) => {
        state.isForgotPasswordLoading = true;
        state.forgotPasswordError = null;
        state.isForgotPasswordSuccess = false;
      })
      .addCase(forgotPassword.success, (state) => {
        state.isForgotPasswordLoading = false;
        state.isForgotPasswordSuccess = true;
        state.forgotPasswordError = null;
      })
      .addCase(forgotPassword.failed, (state, action) => {
        state.isForgotPasswordLoading = false;
        state.isForgotPasswordSuccess = false;
        state.forgotPasswordError = action.payload || "Password reset failed. Please try again.";
      })
      .addCase(resetPassword.request, (state) => {
        state.isResetPasswordLoading = true;
        state.resetPasswordError = null;
        state.isResetPasswordSuccess = false;
      })
      .addCase(resetPassword.success, (state) => {
        state.isResetPasswordLoading = false;
        state.isResetPasswordSuccess = true;
        state.resetPasswordError = null;
      })
      .addCase(resetPassword.failed, (state, action) => {
        state.isResetPasswordLoading = false;
        state.isResetPasswordSuccess = false;
        state.resetPasswordError = action.payload || "Password reset failed. Please try again.";
      })
      .addCase(resendConfirmation.request, (state) => {
        state.isResendConfirmationLoading = true;
        state.resendConfirmationError = null;
        state.isResendConfirmationSuccess = false;
      })
      .addCase(resendConfirmation.success, (state) => {
        state.isResendConfirmationLoading = false;
        state.isResendConfirmationSuccess = true;
        state.resendConfirmationError = null;
      })
      .addCase(resendConfirmation.failed, (state, action) => {
        state.isResendConfirmationLoading = false;
        state.isResendConfirmationSuccess = false;
        state.resendConfirmationError = action.payload || "Failed to resend confirmation email. Please try again.";
      })
      .addCase(updateUser.request, (state) => {
        state.isLoading = true;
        state.error = null; // Clear previous errors
      })
      .addCase(updateUser.success, (state, action) => {
        state.userData = action.payload;
        state.isLoading = false;
        state.error = null;
      })
      .addCase(updateUser.failed, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to update profile. Please try again.";
      })
      // Avatar upload reducers
      .addCase(uploadAvatar.request, (state) => {
        state.isUploadingAvatar = true;
        state.uploadAvatarError = null;
        state.uploadedAvatarUrl = null;
      })
      .addCase(uploadAvatar.success, (state, action) => {
        state.isUploadingAvatar = false;
        state.uploadedAvatarUrl = action.payload;
        state.uploadAvatarError = null;
      })
      .addCase(uploadAvatar.failed, (state, action) => {
        state.isUploadingAvatar = false;
        state.uploadAvatarError = action.payload || "Avatar upload failed. Please try again.";
      })
      // Cookie tracking reducers
      .addCase(cookieStatus.setSent, (state) => {
        state.cookieSetHeaderSent = true;
        state.cookieConfirmed = false;
      })
      .addCase(cookieStatus.setConfirmed, (state) => {
        state.cookieConfirmed = true;
      })
      // Demo login reducers
      .addCase(demoLoginUser.request, (state) => {
        state.isDemoLoggingIn = true;
        state.error = null;
      })
      .addCase(demoLoginUser.success, (state, action) => {
        state.userData = action.payload;
        state.isDemoLoggingIn = false;
        state.error = null;
      })
      .addCase(demoLoginUser.failed, (state, action) => {
        state.isDemoLoggingIn = false;
        state.error = action.payload || 'Demo login failed.';
      });
  },
});

export const { setLoading, clearError, clearSignupSuccess, clearForgotPasswordState, clearResetPasswordState, clearResendConfirmationState } = userSlice.actions;

export default userSlice.reducer;
